import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * CacheBustButton — admin-only pill in the Scorecard header that clears
 * every server-side scorecard cache (ga4_score_cache,
 * digital_property_cache, clarity_cache). Use this AFTER a deploy when
 * a KPI formula has changed and you don't want to wait for the natural
 * 6-24 h TTL to elapse.
 *
 * Non-admin users don't see the button — the endpoint enforces
 * `require_admin`, but hiding it avoids a confusing 403 on the toast.
 * Also invalidates the ``DataFreshnessBadge``'s internal cache indirectly
 * because the badge polls every 60 s anyway.
 */
export default function CacheBustButton({ role }) {
  const [busting, setBusting] = useState(false);

  // Only admins see this button. Everyone else's UI stays clean.
  if (role !== "admin") return null;

  const handleClick = async () => {
    if (busting) return;
    setBusting(true);
    try {
      const r = await api.post("/admin/cache/bust");
      const cleared = r.data?.cleared || {};
      const totalDocs = Object.values(cleared)
        .filter((v) => typeof v === "number")
        .reduce((a, b) => a + b, 0);
      toast.success(
        totalDocs > 0
          ? `Caché limpiado · ${totalDocs} entradas eliminadas. Recarga en 3 s…`
          : "Caché ya estaba vacío. Recargando…",
        { duration: 2800 },
      );
      // Give the toast a moment, then hard-refresh so every card refetches
      // from a cold cache. Simpler than wiring an event bus across cards.
      setTimeout(() => window.location.reload(), 3000);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        e?.message ||
        "No se pudo limpiar el caché. Verifica que seas admin.",
      );
    } finally {
      setBusting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busting}
      data-testid="scorecard-cache-bust"
      title="Limpia los cachés server-side (GA4, DPO, Clarity). Útil tras un deploy o cambio de fórmula."
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all hover:shadow-sm disabled:opacity-50"
      style={{
        borderColor: "#0ea5e955",
        background: busting ? "#0ea5e920" : "#0ea5e914",
        color: "#0369a1",
      }}
    >
      <RefreshCw size={13} className={busting ? "animate-spin" : ""}/>
      {busting ? "Limpiando…" : "Refrescar datos"}
    </button>
  );
}
